import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {AnimationStylesPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/animation-styles-panel/animation-styles-panel';
import {useAppSelector} from '@/hooks';
import {getCurrentPageItems} from '@Components/poster-editor/poster-editor-reducer';
import type {AnimationSpeed, SlideType} from '@PosterWhiteboard/classes/item-animation.class';
import {PositionType} from '@PosterWhiteboard/classes/item-animation.class';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {AnimationType} from '@PosterWhiteboard/items/item/item.types';
import {AnimationTabType} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-animation-option/default-animation-options.types';
import {getConsistentAnimationType} from '@Panels/poster-editing-page-animation-panel/page-animation-panel.util';
import {showMessageGrowl} from '@Components/message-growl';

export function PageAnimationPanel(): ReactElement<any> | null {
  const [animationTab, setAnimationTab] = useState(AnimationTabType.ENTRANCE);
  const currentPageItems = useAppSelector(getCurrentPageItems);

  if (!currentPageItems) {
    throw new Error('Unable to show animation options since the current page is null');
  }

  if (Object.keys(currentPageItems.itemsHashMap).length === 0) {
    return null;
  }

  const items = Object.values(currentPageItems.itemsHashMap);

  const introType = getConsistentAnimationType(items, (item) => item.introAnimation.type);
  const outroType = getConsistentAnimationType(items, (item) => item.outroAnimation.type);
  const introAnimation = (items.find((item) => item.introAnimation.type !== AnimationType.NONE) ?? items[0]).introAnimation;
  const outroAnimation = (items.find((item) => item.outroAnimation.type !== AnimationType.NONE) ?? items[0]).outroAnimation;

  const getSelectedAnimation = (): AnimationType => {
    if (animationTab === AnimationTabType.BOTH) {
      return introType === outroType ? introType : AnimationType.NONE;
    }
    return animationTab === AnimationTabType.EXIT ? outroType : introType;
  };

  const activeAnimation = animationTab === AnimationTabType.EXIT ? outroAnimation : introAnimation;

  const onAnimationTypeModified = (animationType: AnimationType): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      throw new Error('Unable to change animation type since current page is undefined');
    }

    const updateData = {
      type: animationType,
      direction: animationType === AnimationType.ZOOM ? PositionType.LEFT : PositionType.RIGHT,
    };
    const updateOptions = {checkForDurationUpdate: true, replayPosterOnUpdateDone: true};
    const suppressedOptions = {checkForDurationUpdate: false, undoable: false, fireModifiedEvent: false, replayPosterOnUpdateDone: false};

    if (animationTab === AnimationTabType.ENTRANCE || animationTab === AnimationTabType.BOTH) {
      trackPosterBuilderGA4Events(GA4EventName.ANIMATION_DESIGN_ENTRANCE, {[GA4EventParamName.ANIMATION_TYPE]: animationType});
      currentPage.applyIntroAnimationToItems(updateData, animationTab === AnimationTabType.BOTH ? suppressedOptions : updateOptions);
    }
    if (animationTab === AnimationTabType.EXIT || animationTab === AnimationTabType.BOTH) {
      trackPosterBuilderGA4Events(GA4EventName.ANIMATION_DESIGN_EXIT, {[GA4EventParamName.ANIMATION_TYPE]: animationType});
      currentPage.applyOutroAnimationToItems(updateData, animationTab === AnimationTabType.BOTH ? {...updateOptions, replayPosterOnUpdateDone: false} : updateOptions);
    }
    if (animationTab === AnimationTabType.BOTH) {
      void window.posterEditor?.whiteboard?.replayPage(currentPage.hashedID);
    }

    showMessageGrowl({key: 'page-animation-apply', text: window.i18next.t('pmwjs_applied_to_all_items'), interval: 2000});
  };

  const onAnimationSpeedModified = (undoable: boolean, replayPosterOnChange: boolean, speed: AnimationSpeed): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      throw new Error('Unable to change animation type since current page is undefined');
    }

    const updateData = {speed};
    const updateOptions = {checkForDurationUpdate: true, replayPosterOnUpdateDone: replayPosterOnChange, undoable};
    const suppressedOptions = {checkForDurationUpdate: false, undoable: false, fireModifiedEvent: false, replayPosterOnUpdateDone: false};

    if (animationTab === AnimationTabType.ENTRANCE || animationTab === AnimationTabType.BOTH) {
      currentPage.applyIntroAnimationToItems(updateData, animationTab === AnimationTabType.BOTH ? suppressedOptions : updateOptions);
    }
    if (animationTab === AnimationTabType.EXIT || animationTab === AnimationTabType.BOTH) {
      currentPage.applyOutroAnimationToItems(updateData, animationTab === AnimationTabType.BOTH ? {...updateOptions, replayPosterOnUpdateDone: false} : updateOptions);
    }
    if (animationTab === AnimationTabType.BOTH && replayPosterOnChange) {
      void window.posterEditor?.whiteboard?.replayPage(currentPage.hashedID);
    }
  };

  const onAnimationDirectionModified = (direction: SlideType | PositionType): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      throw new Error('Unable to change animation type since current page is undefined');
    }

    const updateData = {direction};
    const updateOptions = {checkForDurationUpdate: true, replayPosterOnUpdateDone: true};
    const suppressedOptions = {checkForDurationUpdate: false, undoable: false, fireModifiedEvent: false, replayPosterOnUpdateDone: false};

    if (animationTab === AnimationTabType.ENTRANCE || animationTab === AnimationTabType.BOTH) {
      currentPage.applyIntroAnimationToItems(updateData, animationTab === AnimationTabType.BOTH ? suppressedOptions : updateOptions);
    }
    if (animationTab === AnimationTabType.EXIT || animationTab === AnimationTabType.BOTH) {
      currentPage.applyOutroAnimationToItems(updateData, animationTab === AnimationTabType.BOTH ? {...updateOptions, replayPosterOnUpdateDone: false} : updateOptions);
    }
    if (animationTab === AnimationTabType.BOTH) {
      void window.posterEditor?.whiteboard?.replayPage(currentPage.hashedID);
    }
  };

  const getAnimations = (): AnimationType[] => {
    const animations = [
      AnimationType.NONE,
      AnimationType.BLOCK,
      AnimationType.BOUNCE,
      AnimationType.FADE,
      AnimationType.JELLO,
      AnimationType.PAN,
      AnimationType.POP,
      AnimationType.ROTATE,
      AnimationType.SCALE,
      AnimationType.SLIDE,
      AnimationType.TUMBLE,
      AnimationType.WIPE,
      AnimationType.PEEK,
      AnimationType.ZOOM,
    ];
    if (animationTab === AnimationTabType.EXIT || animationTab === AnimationTabType.BOTH) {
      return animations.filter((a) => a !== AnimationType.BOUNCE);
    }
    return animations;
  };

  return (
    <AnimationStylesPanel
      selectedAnimation={getSelectedAnimation()}
      onAnimationTypeModified={onAnimationTypeModified}
      onAnimationSpeedModified={onAnimationSpeedModified}
      onAnimationDirectionModified={onAnimationDirectionModified}
      speed={activeAnimation.speed}
      direction={activeAnimation.direction}
      animations={getAnimations()}
      showInGrid={true}
      animationTab={animationTab}
      onAnimationTabChange={setAnimationTab}
    />
  );
}
